import React, { useState } from "react";
import { MapPin, Send } from "lucide-react";
import { prestationOptions, frequenceOptions, greenFrequenceOptions, company } from "../mock";
import { useToast } from "../hooks/use-toast";
import { submitQuote } from "../api";

const initialState = {
  societe: "",
  contact: "",
  email: "",
  telephone: "",
  prestation: "",
  frequence: "",
  surface: "",
  localisation: "",
  details: "",
  surface_pelouse: "",
  lineaire_haies: "",
  heures_estimees: "",
};

const isGreen = (p) => /espaces verts/i.test(p || "");
const isHandy = (p) => /toutes mains/i.test(p || "");

const QuoteForm = React.forwardRef((props, ref) => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitQuote(form);
      setForm(initialState);
      toast({
        title: "Demande envoyée ✓",
        description: "Notre équipe vous répond avec un chiffrage sous 24h.",
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer la demande. Réessayez.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-neutral-300 rounded-md px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition";

  return (
    <section id="devis" ref={ref} className="py-20 md:py-28 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-4">
              Devis gratuit
            </p>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-neutral-900 leading-tight">
              Un besoin ? Un chiffrage <span className="text-brand">sous 24h.</span>
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed max-w-md">
              Remplissez ce formulaire en 60 secondes. Notre équipe commerciale
              vous répond avec une proposition concrète et un planning adapté.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 bg-white border border-neutral-200 rounded-xl px-5 py-4">
              <MapPin size={20} className="text-brand" />
              <div>
                <p className="text-sm font-semibold text-neutral-900">{company.region}</p>
                <p className="text-xs text-neutral-500">{company.zone}</p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Société *</label>
                <input required className={inputCls} value={form.societe} onChange={(e) => update("societe", e.target.value)} placeholder="Votre entreprise" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Contact *</label>
                <input required className={inputCls} value={form.contact} onChange={(e) => update("contact", e.target.value)} placeholder="Nom & prénom" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Email *</label>
                <input required type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@societe.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Téléphone *</label>
                <input required className={inputCls} value={form.telephone} onChange={(e) => update("telephone", e.target.value)} placeholder="06 00 00 00 00" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Prestation *</label>
                <select required className={inputCls} value={form.prestation} onChange={(e) => update("prestation", e.target.value)} data-testid="quote-prestation">
                  <option value="">Choisir une prestation</option>
                  {prestationOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">{isGreen(form.prestation) ? "Fréquence souhaitée" : "Fréquence"}</label>
                <select className={inputCls} value={form.frequence} onChange={(e) => update("frequence", e.target.value)} data-testid="quote-frequence">
                  <option value="">Choisir une fréquence</option>
                  {(isGreen(form.prestation) ? greenFrequenceOptions : frequenceOptions).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              {isGreen(form.prestation) ? (<>
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Surface de pelouse (m²)</label>
                  <input inputMode="numeric" className={inputCls} value={form.surface_pelouse} onChange={(e) => update("surface_pelouse", e.target.value)} placeholder="ex : 1 200" data-testid="quote-surface-pelouse" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Linéaire de haies (m)</label>
                  <input inputMode="numeric" className={inputCls} value={form.lineaire_haies} onChange={(e) => update("lineaire_haies", e.target.value)} placeholder="ex : 80" data-testid="quote-lineaire-haies" />
                </div>
              </>) : isHandy(form.prestation) ? (
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Nombre d'heures estimé</label>
                  <input inputMode="numeric" className={inputCls} value={form.heures_estimees} onChange={(e) => update("heures_estimees", e.target.value)} placeholder="ex : 3" data-testid="quote-heures" />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Surface (m²)</label>
                  <input className={inputCls} value={form.surface} onChange={(e) => update("surface", e.target.value)} placeholder="ex : 350" />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Localisation *</label>
                <input required className={inputCls} value={form.localisation} onChange={(e) => update("localisation", e.target.value)} placeholder="Ville / code postal" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Détails complémentaires</label>
              <textarea rows={3} className={inputCls} value={form.details} onChange={(e) => update("details", e.target.value)} placeholder="Précisez votre besoin..." />
            </div>
            <p className="mt-3 text-xs text-neutral-400">* champs obligatoires</p>
            <button
              type="submit"
              disabled={loading}
              className="mt-5 w-full bg-brand hover:bg-brand-dark text-white font-semibold py-3.5 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {loading ? "Envoi..." : "Envoyer la demande"}
              {!loading && <Send size={16} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
});

export default QuoteForm;
